import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, catchError, map } from 'rxjs';

/** Backend host für SSR — im Browser läuft alles relativ über den Dev-Proxy. */
const SSR_API_BASE = 'http://api:8080';

/**
 * Modus für /suggest und /forward:
 *  - 'address' → vollständige Straßenadressen mit Hausnummer (Adressformulare)
 *  - 'place'   → Städte / Stadtteile / PLZ / Regionen (Orts-/Stadtsuche)
 */
export type PlacesMode = 'address' | 'place';

export interface PlaceComponents {
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  stadt: string | null;
  bezirk: string | null;
  region: string | null;
  land: string | null;
}

export interface PlaceSuggestion {
  mapboxId: string;
  name: string;
  placeFormatted: string;
  fullAddress: string | null;
  featureType: string;
  components: PlaceComponents;
}

export interface PlaceDetails extends PlaceSuggestion {
  lat: number;
  lng: number;
}

interface SuggestResponse {
  items: PlaceSuggestion[];
}

/**
 * Adress-Autocomplete und Geocoding via Backend-Proxy (/api/places/*). Der
 * Provider-Key liegt server-seitig — der Browser sieht ihn nie. Mapbox
 * verwendet Session-Tokens für Billing: alle /suggest-Calls + 1 /retrieve mit
 * gleichem Token zählen als EIN abgerechneter Vorgang. Daher:
 *   const token = placesService.newSessionToken();
 *   placesService.suggest('Berlin', 'place', token).subscribe(...)
 *   // ...später, wenn user etwas auswählt:
 *   placesService.retrieve(picked.mapboxId, token).subscribe(...)
 *   // dann den Token wegwerfen, neuen für nächsten Eingabevorgang holen.
 */
@Injectable({ providedIn: 'root' })
export class PlacesService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);

  /** UUID v4 als Mapbox session_token. */
  newSessionToken(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // Fallback: nicht kryptographisch stark, aber Mapbox akzeptiert jede UUID-Form.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Live-Vorschläge während der Eingabe. Liefert leeres Array bei zu kurzer
   * Query oder Provider-Fehlern — UI muss daraus ein "keine Treffer" / kein
   * Dropdown machen.
   */
  suggest(query: string, mode: PlacesMode, sessionToken: string,
          proximity?: { lat: number; lng: number }): Observable<PlaceSuggestion[]> {
    const trimmed = query?.trim() ?? '';
    if (trimmed.length < 2) return of([]);

    let params = new HttpParams()
      .set('q', trimmed)
      .set('mode', mode)
      .set('sessionToken', sessionToken);
    if (proximity) {
      params = params.set('lat', proximity.lat).set('lng', proximity.lng);
    }

    return this.http
      .get<SuggestResponse>(this.url('/api/places/suggest'), { params })
      .pipe(
        map((res) => res.items ?? []),
        catchError(() => of<PlaceSuggestion[]>([])),
      );
  }

  /**
   * Vollständige Daten + Koordinaten für eine vom User gewählte Suggestion.
   * sessionToken MUSS derselbe wie beim vorhergehenden suggest-Call sein,
   * sonst wird's als zwei getrennte Mapbox-Sessions abgerechnet.
   */
  retrieve(mapboxId: string, sessionToken: string): Observable<PlaceDetails | null> {
    const params = new HttpParams().set('id', mapboxId).set('sessionToken', sessionToken);
    return this.http
      .get<PlaceDetails>(this.url('/api/places/retrieve'), { params })
      .pipe(catchError(() => of<PlaceDetails | null>(null)));
  }

  /**
   * Reverse-Geocoding für Koordinaten → strukturierte Adresse. Wird per
   * Request abgerechnet (kein Session-Billing).
   */
  reverseGeocode(lat: number, lng: number): Observable<PlaceDetails | null> {
    const params = new HttpParams().set('lat', lat).set('lng', lng);
    return this.http
      .get<PlaceDetails | null>(this.url('/api/places/reverse'), { params })
      .pipe(catchError(() => of<PlaceDetails | null>(null)));
  }

  /**
   * Single-Shot Forward-Geocoding: wenn ein Freitext direkt zu Koordinaten
   * aufgelöst werden soll, ohne den Suggest-Roundtrip. Use case:
   * /suche?ort=Berlin direkt per URL geöffnet, ohne dass jemand vorher etwas
   * im Autocomplete angeklickt hat.
   */
  forwardGeocode(query: string, mode: PlacesMode): Observable<PlaceDetails | null> {
    const trimmed = query?.trim() ?? '';
    if (!trimmed) return of(null);
    const params = new HttpParams().set('q', trimmed).set('mode', mode);
    return this.http
      .get<PlaceDetails | null>(this.url('/api/places/forward'), { params })
      .pipe(catchError(() => of<PlaceDetails | null>(null)));
  }

  /** Kurzform "Stadt" oder Fallback aus Komponenten — für Chip-/Tag-Anzeige. */
  static shortLabel(p: { components: PlaceComponents; name: string }): string {
    return p.components.stadt
      ?? p.components.bezirk
      ?? p.components.region
      ?? p.name;
  }

  private url(path: string): string {
    return isPlatformBrowser(this.platformId) ? path : `${SSR_API_BASE}${path}`;
  }
}
